import { supabase, getGeoPos } from './supabase';
import { getDeviceFingerprint } from './deviceFingerprint';

// Modos de registro de entrada/salida elegidos por cada organización (fuente de verdad: el servidor)
export const DEFAULT_ATT = {
  methods: { phone: true, qr_screen: false, kiosk: false },
  phone: { require_location: true, require_biometric: true, biometric_server: false },
  qr: { rotate_seconds: 60, require_location: false },
  network: { restrict: false, allowed_ips: [] },
  window: { enabled: false, before_min: 30, after_min: 120, block_non_working: false },
};

const rpc = async (fn, args) => {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data;
};

export const getAttendanceConfig = async () => {
  const c = await rpc('my_attendance_config');
  return {
    methods: { ...DEFAULT_ATT.methods, ...c?.methods },
    phone: { ...DEFAULT_ATT.phone, ...c?.phone },
    qr: { ...DEFAULT_ATT.qr, ...c?.qr },
    network: { ...DEFAULT_ATT.network, ...c?.network },
    window: { ...DEFAULT_ATT.window, ...c?.window },
  };
};
export const setAttendanceConfig = (p) => rpc('set_my_attendance_config', { p });
export const getMyIp = () => rpc('my_ip');
export const qrCurrentToken = () => rpc('qr_current_token');
export const kioskMark = (pin) => rpc('kiosk_mark', { p_pin: pin });
export const adminSetEmployeePin = (employeeId) => rpc('admin_set_employee_pin', { p_employee: employeeId });
export const adminEmployeesWithPin = async () => (await rpc('admin_employees_with_pin')) || [];
export const adminCreateKiosk = (name, email, password) => rpc('admin_create_kiosk', { p_name: name, p_email: email, p_password: password });
export const adminMarkAttendance = (employeeId, action, atIso) => rpc('admin_mark_attendance', { p_employee: employeeId, p_action: action, p_at: atIso || null });

export const getKioskAccounts = async () => {
  const { data, error } = await supabase.from('profiles').select('id,name,email,active,created_at').eq('role', 'kiosk').order('created_at');
  if (error) throw error;
  return data || [];
};

// Saca el token de un QR leído (URL completa o token suelto)
export const extractQrToken = (text) => {
  const s = String(text || '').trim();
  const m = s.match(/\/q\/([A-Za-z0-9._-]+)/);
  if (m) return m[1];
  return /^[0-9a-f]{32}\.\d{1,12}\.[0-9a-f]{20}$/.test(s) ? s : null;
};

export const qrUrl = (token) => `${window.location.origin}/q/${token}`;

// Marca con el QR leído: agrega huella del dispositivo y, si la organización lo exige, la ubicación
export const markQrScan = async (token, cfg) => {
  let geo = null;
  if (cfg?.qr?.require_location) {
    const pos = await getGeoPos();
    geo = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy };
  }
  const fp = await getDeviceFingerprint().catch(() => null);
  return rpc('mark_qr', {
    p_token: token, p_lat: geo?.lat ?? null, p_lng: geo?.lng ?? null, p_accuracy: geo?.acc ?? null,
    p_device_fingerprint: fp, p_device_mismatch: false,
  });
};

export const METHOD_LABEL = { biometric: 'Huella / Face ID', gps: 'GPS', qr: 'QR', kiosk: 'Kiosco', admin: 'Administrador' };

// Modo de registro por empleado (excepciones a la regla de la organización)
export const adminSetEmployeeAttendance = (employeeId, override) => rpc('admin_set_employee_attendance', { p_employee: employeeId, p_override: override || {} });
export const adminEmployeeOverrides = async () => {
  const rows = (await rpc('admin_employee_overrides')) || [];
  const map = {};
  rows.forEach((r) => { map[r.employee_id] = r.override; });
  return map;
};
export const REMOTE_OVERRIDE = { methods: { phone: true }, skip_network: true, skip_location: true };
export const describeOverride = (o) => {
  if (!o || !Object.keys(o).length) return 'Como la organización';
  if (o.skip_network && o.skip_location && o.methods?.phone && Object.keys(o.methods).length === 1) return 'Remoto (sin red ni sede)';
  return 'Personalizado';
};

// Sedes habilitadas por empleado (vacío = cualquier sede)
export const getMyLocationIds = async () => (await rpc('my_location_ids')) || [];
export const adminSetEmployeeLocations = (employeeId, locationIds) => rpc('admin_set_employee_locations', { p_employee: employeeId, p_locations: locationIds || [] });
export const adminEmployeeLocations = async () => {
  const rows = (await rpc('admin_employee_locations')) || [];
  const map = {};
  rows.forEach((r) => { (map[r.employee_id] = map[r.employee_id] || []).push(r.location_id); });
  return map;
};

// Dispositivos habituales (alerta de marcación desde otro dispositivo)
export const adminTrustDevice = (employeeId, fp) => rpc('admin_trust_device', { p_employee: employeeId, p_fingerprint: fp });
export const adminResetDevices = (employeeId) => rpc('admin_reset_devices', { p_employee: employeeId });
export const getMyAlerts = async () => {
  const { data, error } = await supabase.from('user_alerts').select('id,type,title,body,created_at,read').eq('read', false).order('created_at', { ascending: false }).limit(5);
  if (error) return [];
  return data || [];
};
export const markAlertRead = async (id) => { await supabase.from('user_alerts').update({ read: true }).eq('id', id); };
